/**
 * Optimistic overlay: the confirmed server data plus every op still in the outbox.
 * Applying an op the server has already confirmed is a no-op (the step's lastEventId
 * already equals the op's event), so the overlay is safe whichever arrives first — the
 * live event or the /ops response.
 */
import type { ProjectDTO, StepDTO } from '@pace/contracts';
import type { QueuedOp } from './outbox';

/** What an Undo restores on this device before the server confirms it. */
export type StepSnapshot = Pick<
  StepDTO,
  'state' | 'actual' | 'holdReasonId' | 'note' | 'lastEventId' | 'updatedBy' | 'updatedAt'
>;

export const snapshotOf = (s: StepDTO): StepSnapshot => ({
  state: s.state,
  actual: s.actual,
  holdReasonId: s.holdReasonId,
  note: s.note,
  lastEventId: s.lastEventId,
  updatedBy: s.updatedBy,
  updatedAt: s.updatedAt,
});

function patchStep(projects: ProjectDTO[], stepId: string, fn: (s: StepDTO) => StepDTO | null): ProjectDTO[] {
  let changed = false;
  const out = projects.map((p) => {
    const i = p.steps.findIndex((s) => s.id === stepId);
    if (i < 0) return p;
    const next = fn(p.steps[i]!);
    if (!next) return p;
    changed = true;
    const steps = p.steps.slice();
    steps[i] = next;
    return { ...p, steps };
  });
  return changed ? out : projects;
}

export function applyPending(projects: ProjectDTO[], queue: readonly QueuedOp[]): ProjectDTO[] {
  if (!queue.length) return projects;
  let out = projects;
  for (const { op, userId, prev } of queue) {
    const stamp = { updatedBy: userId, updatedAt: op.at };
    switch (op.type) {
      case 'step.done':
        out = patchStep(out, op.stepId, (s) =>
          s.lastEventId === op.eventId || s.state === 'skipped'
            ? null
            : {
                ...s,
                ...stamp,
                state: 'done',
                actual: op.date,
                holdReasonId: null,
                note: op.note ?? s.note,
                lastEventId: op.eventId,
              },
        );
        break;
      case 'step.hold':
        out = patchStep(out, op.stepId, (s) =>
          s.lastEventId === op.eventId || s.state === 'done' || s.state === 'skipped'
            ? null
            : {
                ...s,
                ...stamp,
                state: 'hold',
                holdReasonId: op.reasonId,
                note: op.note ?? s.note,
                lastEventId: op.eventId,
              },
        );
        break;
      case 'step.release':
        out = patchStep(out, op.stepId, (s) =>
          s.lastEventId === op.eventId || s.state !== 'hold'
            ? null
            : { ...s, ...stamp, state: 'open', holdReasonId: null, lastEventId: op.eventId },
        );
        break;
      case 'step.reopen':
        out = patchStep(out, op.stepId, (s) =>
          s.lastEventId === op.eventId || s.state !== 'done'
            ? null
            : { ...s, ...stamp, state: 'open', actual: null, lastEventId: op.eventId },
        );
        break;
      case 'step.note':
        out = patchStep(out, op.stepId, (s) =>
          s.lastEventId === op.eventId
            ? null
            : { ...s, ...stamp, note: op.note || null, lastEventId: op.eventId },
        );
        break;
      case 'step.undo':
        for (const eventId of op.eventIds) {
          const before = prev?.[eventId] as (StepSnapshot & { stepId: string }) | undefined;
          if (!before) continue;
          out = patchStep(out, before.stepId, (s) =>
            s.lastEventId === eventId ? { ...s, ...before } : null,
          );
        }
        break;
    }
  }
  return out;
}
